import React, { useCallback, useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import CustomerNav from "@/components/layout/CustomerNav";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ChevronLeft, ChevronRight, Loader2, Moon, RotateCcw } from "lucide-react";
import { customerApi, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { isLongevityUnlocked } from "@/lib/LongevityEngine";
import {
  RANGES, NOT_TRACKED, dayHeading, rangeCaption, band,
} from "@/components/longevity/sleep/sleepFields";
import {
  ImpactChips, MoreAboutSleep, QualityRow, ScoreHeader, StageTimeline,
} from "@/components/longevity/sleep/SleepSections";
import {
  MetricChart, MetricChips, MetricHeadline, MetricLegend, NightList,
} from "@/components/longevity/sleep/SleepPeriod";
import {
  BestNightBadge, GoalDialog, GoalSummary,
} from "@/components/longevity/sleep/SleepGoal";

/**
 * Recovery & Sleep detail — opened by tapping the Recovery & Sleep card on
 * the Longevity dashboard. Route: /portal/longevity/recovery-sleep
 *
 * Every figure is derived read-only from the wearable syncs already in the
 * guest's vault (`GET /longevity/me/sleep`). A metric the device never sent
 * says so plainly rather than showing a fabricated number.
 */
export default function SleepDetail() {
  const navigate = useNavigate();
  const { customer } = useAuth();
  const unlocked = isLongevityUnlocked(customer?.email);

  const [range, setRange] = useState("D");
  const [date, setDate] = useState(null);
  const [metricKey, setMetricKey] = useState("total_duration");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [goalOpen, setGoalOpen] = useState(false);
  const [savingGoal, setSavingGoal] = useState(false);

  const load = useCallback(async () => {
    if (!unlocked) return;
    setLoading(true);
    try {
      const params = { range };
      if (date) params.date = date;
      const r = await customerApi.get("/longevity/me/sleep", { params });
      setData(r.data || null);
      if (!date && r.data?.date) setDate(r.data.date);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [range, date, unlocked]);

  useEffect(() => { load(); }, [load]);

  const saveGoal = async (payload) => {
    setSavingGoal(true);
    try {
      await customerApi.put("/longevity/me/sleep-goal", payload);
      setGoalOpen(false);
      toast.success("Sleep goal saved");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingGoal(false);
    }
  };

  const clearGoal = async () => {
    setSavingGoal(true);
    try {
      await customerApi.delete("/longevity/me/sleep-goal");
      setGoalOpen(false);
      toast.success("Sleep goal removed");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingGoal(false);
    }
  };

  if (!unlocked) return <Navigate to="/portal/profile" replace />;

  const night = data?.night || null;
  const duration = night?.duration || null;
  const trend = data?.trend || null;
  const tone = band(night?.score_label);
  // While a tab switch is in flight `data` still holds the previous range's
  // payload — treat that as loading so neither branch reads a missing shape.
  const fresh = data?.range === range;

  const metric = (trend?.metrics || []).find((m) => m.key === metricKey)
    || (trend?.metrics || [])[0] || {};

  // The period arrows step a whole window; the day arrows step one night.
  const stepTo = (d) => setDate(d);

  return (
    <div>
      <CustomerNav />
      <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-10 py-6 sm:py-10">
        <div className="mb-5">
          <Button
            variant="ghost" size="sm" className="rounded-full -ml-2 mb-1 text-xs"
            onClick={() => navigate("/portal/longevity")}
            data-testid="sleep-back-to-dashboard"
          >
            <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> Longevity Overview
          </Button>
          <h1 className="font-editorial text-3xl sm:text-4xl leading-none" data-testid="sleep-heading">
            Recovery &amp; Sleep
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {night?.provider_label
              ? `${night.provider_label} · last sync ${new Date(night.synced_at).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}`
              : "Readings streamed from your Cocoon Health Sync app"}
          </p>
        </div>

        {/* Time range tabs */}
        <div className="flex gap-2 mb-5" data-testid="sleep-range-tabs">
          {RANGES.map((r) => (
            <button
              key={r.key}
              type="button"
              onClick={() => setRange(r.key)}
              className={`flex-1 sm:flex-none sm:min-w-[72px] rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                range === r.key
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted/50 text-muted-foreground hover:bg-muted"
              }`}
              data-testid={`sleep-range-${r.key}`}
            >
              {r.label}
            </button>
          ))}
        </div>

        {/* Date / period header + navigation */}
        <div className="flex items-center justify-between gap-3 mb-6">
          <h2 className="font-editorial text-2xl sm:text-3xl" data-testid="sleep-date-heading">
            {range === "D"
              ? (data?.date ? dayHeading(data.date) : "No nights yet")
              : (data?.period_label || rangeCaption(range, data?.window_days))}
          </h2>
          <div className="flex items-center gap-2">
            <Button
              variant="outline" size="icon" className="rounded-full w-9 h-9"
              disabled={!data?.prev_date}
              onClick={() => stepTo(data.prev_date)}
              data-testid="sleep-prev-day"
              aria-label={range === "D" ? "Previous night" : "Previous period"}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="outline" size="icon" className="rounded-full w-9 h-9"
              disabled={!data?.next_date}
              onClick={() => stepTo(data.next_date)}
              data-testid="sleep-next-day"
              aria-label={range === "D" ? "Next night" : "Next period"}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost" size="icon" className="rounded-full w-9 h-9"
              disabled={!data?.latest_date || data?.date === data?.latest_date}
              onClick={() => stepTo(data.latest_date)}
              data-testid="sleep-latest-night"
              aria-label="Jump to the most recent night"
            >
              <RotateCcw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {loading || !fresh ? (
          <Card className="rounded-2xl">
            <CardContent className="p-10 text-center text-sm text-muted-foreground" data-testid="sleep-loading">
              <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Reading your sleep data…
            </CardContent>
          </Card>
        ) : !data?.has_data ? (
          <Card className="rounded-2xl" data-testid="sleep-empty">
            <CardContent className="p-10 text-center">
              <span className="inline-flex p-3 rounded-full bg-primary/10 text-primary mb-3">
                <Moon className="w-5 h-5" />
              </span>
              <p className="text-sm font-medium">No sleep recorded for this period</p>
              <p className="text-xs text-muted-foreground mt-1 max-w-sm mx-auto">
                {data?.total_nights
                  ? "Use the arrows to move to a night your wearable recorded."
                  : "Open Cocoon Health Sync on your phone and tap Sync now — your nights will appear here."}
              </p>
            </CardContent>
          </Card>
        ) : range === "D" ? (
          <div className="space-y-6">
            {/* Score + what impacted it */}
            <Card className="rounded-2xl" style={{ borderColor: `${tone.ring}33` }}>
              <CardContent className="p-5 sm:p-6 space-y-5">
                <ScoreHeader night={night} />
                <div>
                  <h3 className="text-base font-semibold mb-3" data-testid="sleep-impact-heading">
                    What impacted your score?
                  </h3>
                  <ImpactChips impacts={night.impacts} />
                </div>
              </CardContent>
            </Card>

            {/* Sleep duration */}
            <Card className="rounded-2xl" data-testid="sleep-duration-card">
              <CardContent className="p-5 sm:p-6">
                <h3 className="font-editorial text-2xl mb-3">Sleep duration</h3>
                <div className="flex flex-wrap items-baseline gap-3">
                  <span className="font-editorial text-4xl sm:text-5xl leading-none" data-testid="sleep-duration-total">
                    {duration.asleep_text || "—"}
                  </span>
                  {duration.start_clock && duration.end_clock && (
                    <span className="text-base text-muted-foreground" data-testid="sleep-duration-window">
                      {duration.start_clock}–{duration.end_clock}
                    </span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mt-2 max-w-xl" data-testid="sleep-duration-summary">
                  {duration.summary || NOT_TRACKED}
                </p>
                {duration.goal_text && (
                  <p className="text-xs mt-2" data-testid="sleep-duration-goal">
                    <span className="font-medium">{duration.goal_text}</span>
                    {duration.goal_delta_text && (
                      <span className={duration.goal_met ? "text-[#2f6b45]" : "text-[#8a6321]"}>
                        {" · "}{duration.goal_delta_text}
                      </span>
                    )}
                  </p>
                )}

                <div className="mt-5">
                  <StageTimeline stages={duration.stages} />
                </div>

                {duration.start_clock && duration.end_clock && (
                  <div className="flex items-center justify-between mt-4 text-[11px]">
                    <div>
                      <p className="font-medium">{duration.start_clock}</p>
                      <p className="text-muted-foreground">Time you got to bed</p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium">{duration.end_clock}</p>
                      <p className="text-muted-foreground">Time out of bed</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Sleep quality */}
            <Card className="rounded-2xl" data-testid="sleep-quality-card">
              <CardContent className="p-5 sm:p-6">
                <h3 className="font-editorial text-2xl mb-3">Sleep quality</h3>
                <div className="space-y-3">
                  {(night.quality || []).map((m) => (
                    <QualityRow key={m.key} metric={m} />
                  ))}
                </div>
                <p className="text-[11px] text-muted-foreground mt-3">
                  Dashed band shows the typical range for adults.
                </p>
              </CardContent>
            </Card>

            {/* More about your sleep */}
            <div>
              <h3 className="font-editorial text-2xl mb-3">More about your sleep</h3>
              <MoreAboutSleep more={night.more} />
            </div>

            <GoalSummary
              goal={data.goal}
              nightsMet={null}
              nights={1}
              onEdit={() => setGoalOpen(true)}
            />
          </div>
        ) : (
          <div className="space-y-5" data-testid="sleep-trend-view">
            <BestNightBadge
              best={trend.best_night}
              dayHeading={dayHeading}
              onOpen={(d) => { setDate(d); setRange("D"); }}
            />

            <Card className="rounded-2xl">
              <CardContent className="p-5 sm:p-6 space-y-4">
                <MetricHeadline metric={metric} />
                <MetricChart metric={metric} points={trend.points} />
                <MetricLegend legend={metric.legend} />
              </CardContent>
            </Card>

            <MetricChips
              metrics={trend.metrics}
              active={metricKey}
              onSelect={setMetricKey}
            />

            <GoalSummary
              goal={data.goal}
              nightsMet={trend.goal_nights_met}
              nights={trend.nights}
              onEdit={() => setGoalOpen(true)}
            />

            <NightList
              rows={trend.rows}
              metricKey={metricKey}
              label={data.period_label}
              dayHeading={dayHeading}
              onOpenNight={(d) => { setDate(d); setRange("D"); }}
            />
          </div>
        )}

        <GoalDialog
          open={goalOpen}
          onOpenChange={setGoalOpen}
          goal={data?.goal}
          onSave={saveGoal}
          onClear={clearGoal}
          saving={savingGoal}
        />
      </div>
    </div>
  );
}
